import React from 'react';
import { Alert, Box, Chip, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import Admins from './Admins';
import StoreOperators from './StoreOperators';
import NotificationsSettings from './NotificationsSettings';
import UpdateSettings from './UpdateSettings';
import DriverSettings from './DriverSettings';
import AISettings from './AISettings';
import PubStepClientSettings from './PubStepClientSettings';
import OtpTestPhonesSettings from './OtpTestPhonesSettings';
import OrdersPolicySettings from './OrdersPolicySettings';
import StoreProfileSettings from './StoreProfileSettings';
import DuplicateDetectionSettings from './DuplicateDetectionSettings';
import ProductAvailabilityResetSettings from './ProductAvailabilityResetSettings';
import { DeliveryRulesSettingsPanel } from './DeliveryRulesConfig';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { describeActiveScope } from '../utils/adminScopeUtils';

type ScopeTabKey = 'global' | 'group' | 'store';

type ScopeTabDefinition = {
  label: string;
  content: React.ReactNode;
};

const Settings: React.FC = () => {
  const {
    activeScopeType,
    selectedGroup,
    selectedGroupId,
    selectedStore,
    selectedStoreId,
    stores,
  } = useAdminScope();
  const [tabIndexByScope, setTabIndexByScope] = React.useState<Record<ScopeTabKey, number>>({
    global: 0,
    group: 0,
    store: 0,
  });
  const [mountedTabsByScope, setMountedTabsByScope] = React.useState<Record<ScopeTabKey, number[]>>({
    global: [0],
    group: [0],
    store: [0],
  });

  const globalTabs = React.useMemo<ScopeTabDefinition[]>(
    () => [
      { label: 'Gestion des Admins', content: <Admins /> },
      { label: 'Opérateurs magasin', content: <StoreOperators /> },
      { label: 'Notifications', content: <NotificationsSettings /> },
      { label: 'Mises à jour', content: <UpdateSettings /> },
      { label: 'Politique commandes', content: <OrdersPolicySettings /> },
      { label: 'Reset produits', content: <ProductAvailabilityResetSettings /> },
      { label: 'IA', content: <AISettings /> },
      { label: 'OTP Test', content: <OtpTestPhonesSettings /> },
    ],
    []
  );

  const groupTabs = React.useMemo<ScopeTabDefinition[]>(
    () => [
      {
        label: 'Paramètres livreurs',
        content: (
          <>
            <DriverSettings groupId={selectedGroupId} />
          </>
        ),
      },
      {
        label: 'Tarifs livraison',
        content: <DeliveryRulesSettingsPanel embedded />,
      },
    ],
    [selectedGroupId]
  );

  const storeTabs = React.useMemo<ScopeTabDefinition[]>(
    () => [
      {
        label: 'Pub Step Client',
        content: (
          <PubStepClientSettings
            storeId={selectedStoreId}
            storeName={selectedStore?.name || null}
          />
        ),
      },
      {
        label: 'Profil magasin',
        content: <StoreProfileSettings />,
      },
      {
        label: 'Détection doublons',
        content: (
          <DuplicateDetectionSettings
            storeId={selectedStoreId}
            storeName={selectedStore?.name || null}
          />
        ),
      },
      {
        label: 'Reset produits',
        content: <ProductAvailabilityResetSettings />,
      },
    ],
    [selectedStore?.name, selectedStoreId]
  );

  const currentScopeKey: ScopeTabKey =
    activeScopeType === 'group' ? 'group' : activeScopeType === 'store' ? 'store' : 'global';

  const currentTabs =
    currentScopeKey === 'group' ? groupTabs : currentScopeKey === 'store' ? storeTabs : globalTabs;

  const currentTabIndex = Math.min(tabIndexByScope[currentScopeKey], Math.max(currentTabs.length - 1, 0));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndexByScope((prev) => ({
      ...prev,
      [currentScopeKey]: newValue,
    }));
  };

  React.useEffect(() => {
    setMountedTabsByScope((prev) => {
      const mountedTabs = prev[currentScopeKey];
      if (mountedTabs.includes(currentTabIndex)) {
        return prev;
      }

      return {
        ...prev,
        [currentScopeKey]: [...mountedTabs, currentTabIndex],
      };
    });
  }, [currentScopeKey, currentTabIndex]);

  const scopeSummary = describeActiveScope({
    activeScopeType,
    selectedGroupId,
    selectedStoreId,
    stores,
  });

  if (activeScopeType === 'group' && !selectedGroupId) {
    return (
      <ScopeRequirementAlert
        requiredScope="group"
        title="Sélection de groupe requise"
        description="Choisis d’abord un groupe actif pour accéder aux paramètres de groupe."
      />
    );
  }

  if (activeScopeType === 'store' && !selectedStoreId) {
    return (
      <ScopeRequirementAlert
        requiredScope="store"
        title="Sélection de magasin requise"
        description="Choisis d’abord un magasin actif pour accéder aux paramètres magasin."
      />
    );
  }

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Paramètres
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Les paramètres sont maintenant séparés par scope pour éviter les faux écrans globaux.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip color="primary" label={`Scope: ${activeScopeType}`} />
          <Chip label={scopeSummary} />
          {selectedGroup && <Chip label={`Groupe actif: ${selectedGroup.name}`} />}
          {selectedStore && <Chip label={`Magasin actif: ${selectedStore.name}`} />}
        </Stack>

        {currentScopeKey === 'global' && (
          <Alert severity="info">
            Utilise le scope global pour gérer les réglages communs au réseau: admins,
            opérateurs, notifications, mises à jour, politique commandes, IA et OTP de test.
          </Alert>
        )}
        {currentScopeKey === 'group' && (
          <Alert severity="info">
            Choisis un groupe actif pour gérer les paramètres communs de ses magasins:
            flotte livreurs et tarifs de livraison.
          </Alert>
        )}
        {currentScopeKey === 'store' && (
          <Alert severity="info">
            Choisis un magasin actif pour gérer ses réglages locaux: profil magasin,
            pub Step Client, détection doublons et reset produits.
          </Alert>
        )}
      </Stack>

      <Paper>
        <Tabs value={currentTabIndex} onChange={handleTabChange} aria-label="Paramètres par scope">
          {currentTabs.map((tab) => (
            <Tab key={`${currentScopeKey}-${tab.label}`} label={tab.label} />
          ))}
        </Tabs>
        {currentTabs.map((tab, index) => {
          if (!mountedTabsByScope[currentScopeKey].includes(index)) {
            return null;
          }

          const isActive = index === currentTabIndex;

          return (
            <Box
              key={`${currentScopeKey}-${tab.label}-panel`}
              role="tabpanel"
              hidden={!isActive}
              sx={{
                display: isActive ? 'block' : 'none',
                mt: 2,
                p: 2,
              }}
            >
              {tab.content}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default Settings;
